import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Loading, Panel, Typography } from "@/components/atoms";
import ColorPicker from "@/components/atoms/ColorPicker/ColorPicker";
import { getWidgetHostUrl } from "@/helpers/apiHelpers";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useIsEnterprise, useOperator } from "@/hooks";
import { RequirePermission } from "@/permissions/RequirePermission";
import { EPermission } from "@/permissions/types";
import { useUpdateBrandingMutation } from "@/redux/apis/config/marketing";
import { addToast, clsx } from "@/utils";
import { WidgetModal } from "./WidgetModal";
import { WidgetTheme } from "./type";

// Fallback for when the resize postMessage never arrives (e.g. an older embed build with
// no height reporter) — tall enough to show the full form uncropped.
const PREVIEW_FALLBACK_MIN_HEIGHT = 720;

export const WidgetsPanel = () => {
  const { id, branding } = useOperator();
  // The Instant Quote button colour is an Enterprise-only customization; other plans get
  // the default widget colour (previews + Get HTML still show for everyone).
  const isEnterprise = useIsEnterprise();
  const [updateBranding, { isLoading }] = useUpdateBrandingMutation();

  const [mode, setMode] = useState<"view" | "edit">("view");
  const [color, setColor] = useState(branding.widget);
  // Debounced copy that drives the preview iframes, so dragging in the picker doesn't
  // reload them on every pixel.
  const [previewColor, setPreviewColor] = useState(branding.widget);
  const [modalTheme, setModalTheme] = useState<WidgetTheme | null>(null);

  const lightRef = useRef<HTMLIFrameElement>(null);
  const darkRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setPreviewColor(color), 350);
    return () => clearTimeout(t);
  }, [color]);

  // version=2 mirrors the embed.js snippet (embed.js always appends it); widget_color feeds
  // the operator's chosen button colour so the preview matches the copied code.
  const widgetSrc = (theme: WidgetTheme) =>
    `${getWidgetHostUrl()}/instant-quote/?provider=${id}&version=2&widget_color=${encodeURIComponent(previewColor)}${
      theme === "dark" ? "&theme=dark" : ""
    }`;

  // Widget posts { type: "rm-widget-height", height } once rendered so each preview auto-fits.
  const widgetOrigin = useMemo(() => new URL(widgetSrc("light")).origin, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== widgetOrigin || event.data?.type !== "rm-widget-height") return;
      [lightRef, darkRef].forEach((ref) => {
        if (ref.current && event.source === ref.current.contentWindow) {
          ref.current.style.height = `${event.data.height}px`;
        }
      });
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [widgetOrigin]);

  const handleEdit = () => setMode("edit");

  const handleCancel = () => {
    setColor(branding.widget);
    setMode("view");
  };

  const handleSave = () => {
    updateBranding({ widget_color: color })
      .unwrap()
      .then(() => {
        addToast("success", "Successfully Updated Widget Color");
        setMode("view");
        // Refresh so the saved widget colour flows back through the operator branding.
        setTimeout(() => window.location.reload(), 1250);
      })
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  };

  const renderButtons = () => {
    if (mode === "view") {
      return (
        <Button startIcon="Edit2" onClick={handleEdit}>
          Edit
        </Button>
      );
    }
    return (
      <div className="flex flex-col items-center gap-x-4 gap-y-2 lg:flex-row">
        <Button variant="secondary" onClick={handleCancel} className="w-full">
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} className="w-full">
          Save
        </Button>
      </div>
    );
  };

  const renderColorControl = () => {
    if (mode === "edit") {
      return <ColorPicker hexCode={color} onChange={(c) => setColor(c.hex)} />;
    }
    // View mode: plain swatch + hex (no bordered box), matching the Appearance panel's
    // view-mode convention — the bordered input treatment is reserved for edit mode.
    return (
      <div className="flex flex-row items-center gap-x-3">
        <div className="h-8 w-8 rounded" style={{ backgroundColor: color }} />
        <Typography variant="paragraph">{color}</Typography>
      </div>
    );
  };

  const renderPreview = (theme: WidgetTheme) => {
    const isDark = theme === "dark";
    return (
      <div className="flex flex-col items-center gap-4">
        <Typography variant="action" className="w-full text-left">
          {isDark ? "Dark Mode" : "Light Mode"}
        </Typography>
        {/* Contrasting backdrop so each widget theme is visible: dark backdrop behind the
            light widget, light backdrop behind the dark widget. */}
        <div
          className={clsx(
            // Less backdrop padding on mobile so the widget keeps its width (date/time stay
            // side-by-side) and the card isn't oversized on small screens.
            "flex w-full justify-center rounded-xl p-3 sm:p-5",
            isDark ? "border border-neutral-mid-gray bg-white" : "bg-[#1D2939]",
          )}
        >
          <iframe
            ref={isDark ? darkRef : lightRef}
            className="w-full max-w-[500px]"
            style={{ minHeight: PREVIEW_FALLBACK_MIN_HEIGHT, border: 0 }}
            title={`Instant Quote ${isDark ? "dark" : "light"} preview`}
            src={widgetSrc(theme)}
          />
        </div>
        <Button variant="primary" onClick={() => setModalTheme(theme)}>
          Get HTML Code
        </Button>
      </div>
    );
  };

  return (
    <RequirePermission permission={EPermission.InstantQuoteView}>
      <Panel className="relative flex flex-col">
        {isLoading && <Loading />}
        <div className="flex flex-row items-start gap-4">
          <div className="flex-1">
            <Typography variant="h3">Widgets</Typography>
            <Typography variant="paragraph" className="text-neutral-dark-gray">
              Embed a quotation widget on your website, choose from the options below.
            </Typography>
          </div>
          {isEnterprise && renderButtons()}
        </div>

        {isEnterprise && (
          <div className="mt-8 flex flex-col gap-3 border-b border-neutral-gray pb-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col">
              <Typography variant="action">Instant Quote Button Color</Typography>
              <Typography variant="paragraph" className="text-neutral-dark-gray">
                Choose a color for your instant quote widget&apos;s button
              </Typography>
            </div>
            {renderColorControl()}
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 gap-6 lg:mt-8 lg:grid-cols-2 lg:gap-8">
          {renderPreview("light")}
          {renderPreview("dark")}
        </div>

        <WidgetModal open={modalTheme !== null} onClose={() => setModalTheme(null)} theme={modalTheme ?? "light"} />
      </Panel>
    </RequirePermission>
  );
};
